import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ComponentProps,
} from 'react';
import { useToken } from '@/common/helper';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { filtersConfig } from '@/common/static_data';

import FilterDropdowns from '@/pages/Shipments/Filters/FilterDropdowns';
import {
  MainFilterMenu,
  CalendarFilterMenu,
  type FilterOption,
} from '@/pages/Shipments/Filters/FilterDropdownMenus';
import SubFilterMenu from '@/pages/Shipments/Filters/components/SubFilterMenu';
import AppliedFilterPills from '@/pages/Shipments/Filters/AppliedFilterPills';
import useDateRangeApplyPost from '@/hooks/useDateRangeApplyPost';
import {
  FilterLogic,
  getAppliedFilterLogic,
} from '@/pages/Shipments/Filters/filterLogic';

import type {
  AppliedFilter,
  FilterArrayItem,
  ListItem,
  FilterItem,
  PaginatedFetcher,
  FilterLookups,
} from '@/pages/Shipments/ControlTower/types';
import type {
  FilterControlFlowData,
  FilterDateRange,
} from '@/proto/logiblocks/logiblocks';
import type {
  OnChangePayload,
  AlertSection,
} from '@/pages/Shipments/Filters/CustomAlertThresholdFilter';
import type {
  FilterControlFlowField,
  FilterFieldOption,
  AlertPhaseOption,
} from '@/apis/logiblocksAPI';
import { getFilterControlFlowFields } from '@/apis/logiblocksAPI';

/** State option with cities - used by API for state filters */
interface StateOptionWithCities {
  value: string;
  label: string;
  cities?: Array<{
    value: string;
    label: string;
    display?: string;
  }>;
}

/** State/City hierarchical structure for origin_state and destination_state filters */
type City = {
  id: string | number;
  name?: string;
  display?: string;
};

type StateRow = {
  id: string | number;
  name?: string;
  display_name?: string;
  cities?: City[];
};

/** All filter groups from ControlTower, minus "saved filter sets" (not applicable in node context) */
const NODE_TOP_LEVEL_FILTERS = filtersConfig.topLevelFilters.filter(
  (f) => f.key !== 'saved_filters'
);
// Sensor manufacturer filter is Control Tower / Download Report / GxP only —
// FilterControlFlowData has no sensor field and runtime apply does not support it yet.
const NODE_GROUPED_FILTERS = filtersConfig.groupedFilters.map((group) => {
  if (group.group !== 'Shipment') return group;
  return {
    ...group,
    items: group.items.filter((item) => item.key !== 'sensor'),
  };
});

/**
 * Filter keys whose first-page options come from prefetchedFilterFields (simple list).
 * Paginated keys (from_location, to_location, origin_state, destination_state) use
 * a different code path below to handle LocationRow/StateRow structures, while scroll/
 * load-more is handled by SubFilterMenu via fetcherOverrides → getFilterControlFlowFields pagination.
 * alert_types is NOT included here - it has its own special handling via alertFilterConfigOverride.
 * Date fields have their own renderers.
 */
const PREFETCHABLE_FILTER_KEYS = new Set([
  'team',
  'location_type',
  'project_id',
  'status',
  'type',
  'delivery_status',
  'pod_status',
  'temperature',
  'last_open_alert_aging',
  'courier',
  'product',
  'product_type', // Added: now fetched via API instead of Redux
]);

/**
 * Paginated filter keys whose first page comes from prefetch, but have complex
 * structures (LocationRow[], StateRow[]) that are passed through as-is.
 */
const PAGINATED_LOCATION_KEYS = new Set([
  'from_location',
  'to_location',
  'origin_state',
  'destination_state',
]);

/** Map a FilterFieldOption[] (value/label) to ListItem[] (id/name). */
function simpleOptionsToListItems(options: FilterFieldOption[]): ListItem[] {
  return options.map((o) => ({ id: o.value, name: o.label, title: o.label }));
}

/**
 * Transform API state structure to Control Tower StateRow format.
 * API uses { value, label, cities: [{ value, label, display }] }
 * Control Tower expects { id, display_name, cities: [{ id, display, name }] }
 */
function transformStateOptions(options: StateOptionWithCities[]): StateRow[] {
  return options.map((state) => ({
    id: state.value,
    name: state.label,
    display_name: state.label,
    cities: (state.cities || []).map((city) => ({
      id: city.value,
      name: city.label,
      display: city.display || city.label,
    })),
  }));
}

/**
 * Transform alert_types API structure into AlertSection[] format for CustomAlertThresholdFilter.
 * Converts { phase, phase_display, alerts: [{ value, label, is_critical_type, ... }] }
 * into the grouped format expected by the filter dropdown component.
 */
function transformAlertTypesToFilterConfig(
  options: AlertPhaseOption[]
): AlertSection[] {
  if (!Array.isArray(options) || options.length === 0) return [];

  const criticalAlerts: { id: string; title: string; icon?: boolean }[] = [];
  const atRiskAlerts: { id: string; title: string; icon?: boolean }[] = [];
  const dynamicPhases: AlertSection[] = [];

  options.forEach((phaseGroup) => {
    if (!phaseGroup.alerts || !Array.isArray(phaseGroup.alerts)) return;

    const phaseCritical: { id: string; title: string; icon?: boolean }[] = [];
    const phaseAtRisk: { id: string; title: string; icon?: boolean }[] = [];

    phaseGroup.alerts.forEach((alert) => {
      const item = {
        id: alert.value,
        title: alert.label,
        icon: false, // AI icon - not provided by API
      };

      if (alert.is_critical_type === true) {
        criticalAlerts.push(item);
        phaseCritical.push(item);
      } else {
        atRiskAlerts.push(item);
        phaseAtRisk.push(item);
      }
    });

    // Only add phase section if it has at-risk alerts (critical are grouped separately)
    if (phaseAtRisk.length > 0) {
      dynamicPhases.push({
        type: 'category',
        label: phaseGroup.phase_display,
        items: phaseAtRisk,
      });
    }
  });

  const config: AlertSection[] = [
    {
      type: 'section',
      label: 'Alert state',
      items: [
        { id: 'open', title: 'Open' },
        { id: 'dismissed', title: 'Dismissed' },
        { id: 'resolved', title: 'Resolved' },
      ],
    },
    {
      type: 'section',
      label: 'With AI',
      items: [
        { id: 'processing', title: 'Processing' },
        { id: 'needs_attention', title: 'Needs attention' },
        { id: 'resolved_via_ai', title: 'Resolved via AI' },
      ],
    },
    {
      type: 'section',
      label: 'Critical alerts',
      toggleKey: 'showCritical',
      colorDot: 'var(--danger)',
      items: criticalAlerts,
    },
    {
      type: 'section',
      label: 'At-risk alerts',
      toggleKey: 'showAtRisk',
      colorDot: '#F6BC2F',
      linkedIds: atRiskAlerts.map((a) => a.id),
    },
    ...dynamicPhases,
  ];

  return config;
}

/**
 * Build a map from field_name → ListItem[] | StateRow[] from the prefetched filter fields.
 * Handles simple options, location options, and hierarchical state/city structures.
 */
function buildPrefetchedLookup(
  fields: FilterControlFlowField[] | undefined | null
): Map<string, ListItem[] | StateRow[]> {
  const map = new Map<string, ListItem[] | StateRow[]>();
  if (!fields) return map;

  for (const field of fields) {
    const isSimplePrefetch = PREFETCHABLE_FILTER_KEYS.has(field.field_name);
    const isPaginatedLocation = PAGINATED_LOCATION_KEYS.has(field.field_name);

    if (!isSimplePrefetch && !isPaginatedLocation) continue;
    if (!Array.isArray(field.options) || field.options.length === 0) continue;

    // Special handling for state filters with hierarchical city structure
    if (
      field.field_name === 'origin_state' ||
      field.field_name === 'destination_state'
    ) {
      const stateRows = transformStateOptions(
        field.options as FilterFieldOption[]
      );
      map.set(field.field_name, stateRows);
      continue;
    }

    if (field.field_name === 'courier') {
      // Courier options arrive as { value, label, service_levels } from the API.
      // Normalise to { id, name } so lookups by id work correctly.
      map.set(
        field.field_name,
        (field.options as FilterFieldOption[]).map((o) => ({
          id: o.value,
          name: o.label,
          title: o.label,
        }))
      );
    } else if (isPaginatedLocation) {
      // Location options (from_location, to_location) arrive as LocationRow[]
      // with { id, name/display_name, ... }. Pass through as-is.
      map.set(field.field_name, field.options as ListItem[]);
    } else {
      // Simple { value, label }[] options
      map.set(
        field.field_name,
        simpleOptionsToListItems(field.options as FilterFieldOption[])
      );
    }
  }

  return map;
}

const logicFields: Record<string, keyof FilterControlFlowData> = {
  team: 'team_logic',
  location_type: 'location_type_logic',
  alert_types: 'alert_types_logic',
  product: 'product_logic',
  product_type: 'product_type_logic',
  project_id: 'project_id_logic',
};

/** Rebuild filter UI state from the saved individual-field node data */
function nodeDataToFilterState(fc: FilterControlFlowData | undefined): {
  filterArray: FilterArrayItem[];
  appliedFilters: AppliedFilter[];
} {
  const filterArray: FilterArrayItem[] = [];
  const appliedFilters: AppliedFilter[] = [];

  if (!fc) return { filterArray, appliedFilters };

  const arrayFields: Array<[keyof FilterControlFlowData, string]> = [
    ['status', 'status'],
    ['team', 'team'],
    ['from_location', 'from_location'],
    ['to_location', 'to_location'],
    ['origin_state', 'origin_state'],
    ['destination_state', 'destination_state'],
    ['location_type', 'location_type'],
    ['alert_types', 'alert_types'],
    ['type', 'type'],
    ['last_open_alert_aging', 'last_open_alert_aging'],
    ['delivery_status', 'delivery_status'],
    ['pod_status', 'pod_status'],
    ['product', 'product'],
    ['product_type', 'product_type'],
    ['project_id', 'project_id'],
    ['temperature', 'temperature'],
    ['courier', 'courier'],
    ['link_code', 'link_code'],
  ];

  for (const [dataKey, filterKey] of arrayFields) {
    const values = fc[dataKey] as string[] | undefined;
    if (!values?.length) continue;

    let logic: FilterLogic | undefined;
    const logicKey = logicFields[filterKey];
    if (logicKey) {
      const storedLogic = fc[logicKey] as string | undefined;
      logic = storedLogic === 'AND' ? 'AND' : 'OR';
    }

    filterArray.push({
      filterBy: filterKey,
      filterValue: [...values],
      ...(logic ? { logic } : {}),
    });
    values.forEach((id) =>
      appliedFilters.push({
        id,
        filterBy: filterKey,
        ...(logic ? { logic } : {}),
      })
    );
  }

  if (fc.archived) {
    filterArray.push({ filterBy: 'archived', filterValue: [fc.archived] });
    appliedFilters.push({ id: fc.archived, filterBy: 'archived' });
  }

  const dateFields: Array<[keyof FilterControlFlowData, string]> = [
    ['scheduled_pickup_date', 'scheduled_pickup_date'],
    ['scheduled_delivery_date', 'scheduled_delivery_date'],
    ['created_at', 'created_at'],
  ];

  for (const [dataKey, filterKey] of dateFields) {
    const map = fc[dataKey] as { [key: number]: FilterDateRange } | undefined;
    if (!map || !Object.keys(map).length) continue;
    const ids: string[] = [];
    for (const dr of Object.values(map) as FilterDateRange[]) {
      const id = JSON.stringify({
        period: dr.period,
        start: dr.start,
        end: dr.end,
      });
      ids.push(id);
      appliedFilters.push({
        id,
        filterBy: filterKey,
        dateFilter: filterKey,
        start: dr.start,
        end: dr.end,
        period: dr.period,
        name: dr.period || `${dr.start} – ${dr.end}`,
      });
    }
    filterArray.push({ filterBy: filterKey, filterValue: ids });
  }

  return { filterArray, appliedFilters };
}

export const FilterControlFlowCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
  prefetchedFields,
  prefetchedFieldsParentId,
  workflowId,
}) => {
  const { token } = useToken();
  // Fields and pagination identity come from the panel prop — not from node data.
  const prefetchedFilterFields = prefetchedFields as
    FilterControlFlowField[] | undefined;

  /** Alert filter config built from prefetched alert_types field */
  const alertFilterConfig = useMemo(() => {
    const alertField = prefetchedFilterFields?.find(
      (f) => f.field_name === 'alert_types'
    );
    if (!alertField || !Array.isArray(alertField.options)) return undefined;
    // Type assertion: alert_types field options are always AlertPhaseOption[]
    return transformAlertTypesToFilterConfig(
      alertField.options as AlertPhaseOption[]
    );
  }, [prefetchedFilterFields]);

  // DO NOT use useFilterLookups — it makes external API calls for couriers, teams, etc.
  // All filter data comes from prefetchedFields (via getFilterControlFlowFields API).
  // Populate lookups from prefetchedLookup to prevent Control Tower components from
  // thinking data is missing and triggering external API calls as fallback.
  const lookups: FilterLookups = useMemo(() => {
    const lookup = buildPrefetchedLookup(prefetchedFilterFields);

    // Build flat alert types list for pill display
    const alertTypesList: ListItem[] = [];
    if (alertFilterConfig) {
      alertFilterConfig.forEach((section) => {
        if (Array.isArray(section.items)) {
          section.items.forEach((item) => {
            alertTypesList.push({
              id: item.id,
              name: item.title,
              title: item.title,
            });
          });
        }
      });
    }

    return {
      couriersList: (lookup.get('courier') ?? []) as ListItem[],
      productTypeList: (lookup.get('product_type') ?? []) as ListItem[],
      productList: (lookup.get('product') ?? []) as ListItem[],
      projectIdList: (lookup.get('project_id') ?? []) as ListItem[],
      allTeams: (lookup.get('team') ?? []) as ListItem[],
      shipmentStatusDDL: (lookup.get('status') ?? []) as ListItem[],
      statesAndCities: [] as ListItem[], // Not used in FilterControlFlow
      locationTypeList: (lookup.get('location_type') ?? []) as ListItem[],
      alertTypesDDL: alertTypesList, // Alert types for pill display
      fetchers: {}, // Empty fetchers object - all pagination handled by subFilterFetcherOverrides
    };
  }, [prefetchedFilterFields, alertFilterConfig]);

  /**
   * Generic paginated fetcher for ANY filterControlFlow field.
   * Called by SubFilterMenu on scroll/load-more instead of the internal location APIs.
   * Payload matches: { search, page, paginated_field }
   */
  const makeNodeFieldsFetcher = useCallback(
    (fieldKey: string): PaginatedFetcher<ListItem> =>
      async ({ page, search }) => {
        if (!token || !workflowId || !prefetchedFieldsParentId) {
          return { data: [], pageCount: 1 };
        }
        const fields = await getFilterControlFlowFields(token, {
          search: search ?? '',
          page,
          paginated_field: fieldKey,
        });
        // API returns single field or array - normalize to array
        const fieldArray = Array.isArray(fields) ? fields : [fields];
        const field = fieldArray[0] as FilterControlFlowField;
        const options = field?.options ?? [];
        const pageCount = field?.pagination?.total_pages ?? 1;

        if (options.length === 0) return { data: [], pageCount };

        // Detect option format and transform accordingly:
        const firstOpt = options[0] as Record<string, unknown>;

        // State filters with hierarchical city structure
        // (origin_state, destination_state)
        if ('cities' in firstOpt && Array.isArray(firstOpt.cities)) {
          const stateRows = transformStateOptions(
            options as FilterFieldOption[]
          );
          return { data: stateRows as ListItem[], pageCount };
        }

        // Simple options have { value, label } (e.g. product_type, team)
        if ('value' in firstOpt) {
          const simple = options as FilterFieldOption[];
          return {
            data: simple.map((o) => ({
              id: o.value,
              name: o.label,
              title: o.label,
            })),
            pageCount,
          };
        }

        // Raw location objects (from_location, to_location) — pass through unchanged.
        return { data: options as ListItem[], pageCount };
      },
    [token, workflowId, prefetchedFieldsParentId]
  );

  /**
   * Passed to SubFilterMenu to replace internal location/grouped-location fetchers.
   * Both `locations` (origin_state, destination_state) and `grouped_locations`
   * (from_location, to_location) route to getFilterControlFlowFields instead of location APIs.
   * Recreated when filterBy changes so the correct paginated_field is in the closure.
   */

  /** Lookup map: field_name → ListItem[] built from the prefetched API response */
  const prefetchedLookup = useMemo(() => {
    return buildPrefetchedLookup(prefetchedFilterFields);
  }, [prefetchedFilterFields]);

  /** Display name lookup from prefetched fields: field_name → display_name */
  const prefetchedDisplayNames = useMemo<Record<string, string>>(() => {
    if (!prefetchedFilterFields) return {};
    return prefetchedFilterFields.reduce<Record<string, string>>((acc, f) => {
      acc[f.field_name] = f.display_name;
      return acc;
    }, {});
  }, [prefetchedFilterFields]);

  /** Date field keys detected from prefetched data (type === 6 = DATE_TIME). */
  const dateKeys = useMemo(() => {
    const s = new Set<string>();
    prefetchedFilterFields?.forEach((f) => {
      if (f.type === 6) s.add(f.field_name);
    });
    return s;
  }, [prefetchedFilterFields]);

  /** id→name map per filterBy key, built exclusively from prefetched data. */
  const filterLookupMap = useMemo<
    Record<string, Record<string, string>>
  >(() => {
    const makeMap = (
      list: { id: string; name?: string; title?: string }[] | undefined
    ): Record<string, string> =>
      (list ?? []).reduce<Record<string, string>>((acc, item) => {
        acc[String(item.id)] = item.name ?? item.title ?? String(item.id);
        return acc;
      }, {});

    const result: Record<string, Record<string, string>> = {
      archived: { ARCHIVED: 'Archived' },
      link_code: { link_code: 'Linked shipments' },
    };

    prefetchedLookup.forEach((items, key) => {
      result[key] = makeMap(items as ListItem[]);
    });

    // Resolve alert_types display names from lookups.alertTypesDDL
    result['alert_types'] = makeMap(lookups.alertTypesDDL);

    return result;
  }, [prefetchedLookup, lookups.alertTypesDDL]);

  const [filterArray, setFilterArray] = useState<FilterArrayItem[]>(
    () => nodeDataToFilterState(nodeData.filter_control_flow).filterArray
  );
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>(
    () => nodeDataToFilterState(nodeData.filter_control_flow).appliedFilters
  );

  /**
   * Applied filters enriched with display names resolved from DDL lookups.
   * Bare entries (restored from node data) only have { id, filterBy }; this
   * memo adds the `name` field so PillList can show the filter value instead
   * of falling back to the filterBy category label.
   * Used only for display — the raw appliedFilters state drives emitChange.
   */
  const enrichedAppliedFilters = useMemo<AppliedFilter[]>(() => {
    return appliedFilters.map((filter) => {
      // Already has a display name (set during interactive selection)
      if (
        filter.name ||
        filter.title ||
        filter.courier_name ||
        filter.label ||
        filter.value
      ) {
        return filter;
      }
      const filterKey = filter.filterBy ?? filter.dateFilter ?? '';
      const idMap = filterLookupMap[filterKey];
      const resolved = idMap?.[String(filter.id)];
      // Resolve from DDL if available, otherwise fall back to the id string
      // (still better than showing the category label via toLabel(filterBy))
      return { ...filter, name: resolved ?? String(filter.id) };
    });
  }, [appliedFilters, filterLookupMap]);

  const [filterLogicMap, setFilterLogicMap] = useState<
    Record<string, FilterLogic>
  >(() => {
    const { filterArray: fa } = nodeDataToFilterState(
      nodeData.filter_control_flow
    );
    const initialLogicMap: Record<string, FilterLogic> = {};
    fa.forEach((item) => {
      if (item.logic) {
        initialLogicMap[item.filterBy] = item.logic;
      }
    });
    return initialLogicMap;
  });
  // Dropdown UI state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [secondFilterDisplay, setSecondFilterDisplay] = useState(false);
  const [filterBy, setFilterBy] = useState('');
  const [filterTitle, setFilterTitle] = useState('');
  const [isDatePicker, setIsDatePicker] = useState(false);
  const [secondaryFiltersDDL, setSecondaryFiltersDDL] = useState<ListItem[]>(
    []
  );
  const [secondaryFiltersSource, setSecondaryFiltersSource] = useState<
    ListItem[]
  >([]);
  const [selectedOptions, setSelectedOptions] = useState<ListItem[]>([]);
  const [openedFromPill, setOpenedFromPill] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const dynamicDropdownHeight = 'calc(100vh - 350px)';

  // SubFilterMenu fetcher overrides — route ALL pagination to getFilterControlFlowFields instead
  // of the internal APIs. This ensures ZERO external API calls are made for filters.
  // Create fetchers dynamically for ALL possible filters to support pagination on any field.
  const subFilterFetcherOverrides = useMemo(() => {
    const allFetchers: Record<string, PaginatedFetcher<ListItem>> = {};

    // Create fetchers for all prefetchable filters (may need pagination if large datasets)
    PREFETCHABLE_FILTER_KEYS.forEach((key) => {
      allFetchers[key] = makeNodeFieldsFetcher(key);
    });

    // Create fetchers for all paginated location filters
    PAGINATED_LOCATION_KEYS.forEach((key) => {
      allFetchers[key] = makeNodeFieldsFetcher(key);
    });

    return {
      // Specific routing for location filter types used by useSubFilterMenuState
      locations: allFetchers[filterBy] ?? makeNodeFieldsFetcher(filterBy),
      grouped_locations:
        allFetchers[filterBy] ?? makeNodeFieldsFetcher(filterBy),
      // All individual filter fetchers to prevent fallback to lookups.fetchers
      ...allFetchers,
    };
  }, [makeNodeFieldsFetcher, filterBy]);
  const [submenuAnchor, setSubmenuAnchor] = useState<{
    left: number;
    top: number;
    el: HTMLElement;
  } | null>(null);

  const pendingEmitRef = useRef(false);
  // Tracks whether the current nodeData change was triggered by this component
  // itself via emitChange. If so, we skip re-syncing appliedFilters to preserve
  // the display names that were set during interactive selection.
  const selfUpdateRef = useRef(false);

  // Re-sync when the active node changes (e.g. user switches to a different node).
  // Skip when the change was triggered by emitChange in this component — in that
  // case state is already correct and names from interactive selection are intact.
  useEffect(() => {
    if (selfUpdateRef.current) {
      selfUpdateRef.current = false;
      return;
    }
    const { filterArray: fa, appliedFilters: af } = nodeDataToFilterState(
      nodeData.filter_control_flow
    );
    setFilterArray(fa);
    setAppliedFilters(af);

    const nextLogicMap: Record<string, FilterLogic> = {};
    fa.forEach((item) => {
      if (item.logic) {
        nextLogicMap[item.filterBy] = item.logic;
      }
    });
    setFilterLogicMap(nextLogicMap);
  }, [nodeData.filter_control_flow]);

  /** Persist current filter state to nodeData */
  const emitChange = useCallback(
    (nextFA: FilterArrayItem[], nextApplied?: AppliedFilter[]) => {
      const currentApplied = nextApplied ?? appliedFilters;

      const getIds = (key: string): string[] => {
        const item = nextFA.find((f) => f.filterBy === key);
        if (!item) return [];
        const fv = item.filterValue;
        const arr = Array.isArray(fv) ? fv : [fv];
        return arr.filter(Boolean).map(String);
      };

      /** Mirror ControlTower's buildShipmentFiltersPayload date-bucket logic */
      const parseDateRange = (
        key: string
      ): { [key: number]: FilterDateRange } => {
        const ids = new Set(getIds(key));
        if (!ids.size) return {};
        const matches = currentApplied.filter(
          (a) =>
            (a.dateFilter === key || a.filterBy === key) &&
            ids.has(String(a.id))
        );
        if (!matches.length) return {};
        const bucket: { [key: number]: FilterDateRange } = {};
        matches.forEach((a, index) => {
          let parsedId: {
            period?: string;
            start?: string;
            end?: string;
          } | null = null;
          try {
            parsedId = JSON.parse(String(a.id));
          } catch {
            parsedId = null;
          }
          const period =
            typeof a.period === 'string' ? a.period : parsedId?.period;
          const start = typeof a.start === 'string' ? a.start : parsedId?.start;
          const end = typeof a.end === 'string' ? a.end : parsedId?.end;
          if (start || end) {
            bucket[index] = {
              period: period ?? '',
              start: start ?? '',
              end: end ?? '',
            };
          }
        });
        return bucket;
      };

      const getLogic = (key: string): string => {
        const item = nextFA.find((f) => f.filterBy === key);
        return item?.logic === 'AND' ? 'AND' : 'OR';
      };

      selfUpdateRef.current = true;
      onChange({
        filter_control_flow: {
          subgraph_index: nodeData.filter_control_flow?.subgraph_index ?? -1,
          team: getIds('team'),
          team_logic: getLogic('team'),
          from_location: getIds('from_location'),
          to_location: getIds('to_location'),
          origin_state: getIds('origin_state'),
          destination_state: getIds('destination_state'),
          location_type: getIds('location_type'),
          location_type_logic: getLogic('location_type'),
          alert_types: getIds('alert_types'),
          alert_types_logic: getLogic('alert_types'),
          type: getIds('type'),
          last_open_alert_aging: getIds('last_open_alert_aging'),
          status: getIds('status'),
          delivery_status: getIds('delivery_status'),
          pod_status: getIds('pod_status'),
          product: getIds('product'),
          product_logic: getLogic('product'),
          product_type: getIds('product_type'),
          product_type_logic: getLogic('product_type'),
          project_id: getIds('project_id'),
          project_id_logic: getLogic('project_id'),
          temperature: getIds('temperature'),
          courier: getIds('courier'),
          link_code: getIds('link_code'),
          scheduled_pickup_date: parseDateRange('scheduled_pickup_date'),
          scheduled_delivery_date: parseDateRange('scheduled_delivery_date'),
          created_at: parseDateRange('created_at'),
          archived: getIds('archived')[0] ?? '',
        },
      });
    },
    [nodeData, onChange, appliedFilters]
  );

  // Emit after date-range hook updates state
  useEffect(() => {
    if (!pendingEmitRef.current) return;
    pendingEmitRef.current = false;
    emitChange(filterArray, appliedFilters);
  }, [filterArray, appliedFilters, emitChange]);

  const handleFilterClick = useCallback(
    (filter: FilterOption) => {
      setOpenedFromPill(false);

      // ARCHIVED (id=14) and link_code (id=13) are direct-toggle filters —
      // clicking them immediately applies without opening a secondary dropdown.
      if (filter?.id === 14 || filter?.id === 13) {
        const filterKey = filter.id === 14 ? 'archived' : 'link_code';
        const alreadyApplied = appliedFilters.some(
          (a) => a.filterBy === filterKey
        );
        if (!alreadyApplied) {
          const nextApplied: AppliedFilter[] = [
            ...appliedFilters,
            { id: filter.key, name: filter.label, filterBy: filterKey },
          ];
          const nextFA: FilterArrayItem[] = [
            ...filterArray.filter((f) => f.filterBy !== filterKey),
            { filterBy: filterKey, filterValue: [filter.key] },
          ];
          setAppliedFilters(nextApplied);
          setFilterArray(nextFA);
          emitChange(nextFA, nextApplied);
        }
        setIsFilterOpen(false);
        return;
      }

      const key = filter.key;
      const title = prefetchedDisplayNames[key] ?? filter.label;

      const existingLogic = getAppliedFilterLogic(
        key,
        appliedFilters,
        filterArray
      );
      setFilterLogicMap((prev) => ({ ...prev, [key]: existingLogic }));

      setFilterBy(key);
      setIsFilterOpen(false);

      // Date fields — show calendar picker, no list needed.
      if (dateKeys.has(key)) {
        setFilterTitle(title);
        setIsDatePicker(true);
        setSecondaryFiltersDDL([]);
        setSecondaryFiltersSource([]);
        setTimeout(() => setSecondFilterDisplay(true), 0);
        return;
      }

      // All other filters — populate from prefetchedLookup.
      // Both simple filters AND paginated filters (from_location, to_location, etc.)
      // get their initial page here. SubFilterMenu then handles scroll/load-more
      // pagination via fetcherOverrides → getFilterControlFlowFields for paginated fields.
      const options = prefetchedLookup.get(key) ?? [];
      setFilterTitle(title);
      setIsDatePicker(false);
      // Cast to ListItem[] for compatibility (StateRow[] is also compatible)
      setSecondaryFiltersDDL(options as ListItem[]);
      setSecondaryFiltersSource(options as ListItem[]);
      setTimeout(() => setSecondFilterDisplay(true), 0);
    },
    [
      appliedFilters,
      filterArray,
      emitChange,
      prefetchedLookup,
      prefetchedDisplayNames,
      dateKeys,
    ]
  );

  const handleApplyFilters = useCallback(() => {
    if (!filterBy) return;

    const items = selectedOptions;
    const desiredIds = items.map((o) => o.id);
    const desiredIdSet = new Set(desiredIds);

    const appliedHere = appliedFilters.filter(
      (a) => a.filterBy === filterBy || a.dateFilter === filterBy
    );
    const appliedIdSet = new Set(appliedHere.map((a) => a.id));

    const toRemoveIds = Array.from(appliedIdSet).filter(
      (id) => !desiredIdSet.has(id)
    );
    const toAddIds = Array.from(desiredIdSet).filter(
      (id) => !appliedIdSet.has(id)
    );

    const logic = filterLogicMap[filterBy] || 'OR';

    const nextApplied: AppliedFilter[] = [
      ...appliedFilters.filter(
        (a) =>
          !(
            (a.filterBy === filterBy || a.dateFilter === filterBy) &&
            toRemoveIds.includes(a.id)
          )
      ),
      ...items
        .filter((o) => toAddIds.includes(o.id))
        .map((o) => ({ ...o, filterBy, logic })),
    ];

    const others = filterArray.filter((f) => f.filterBy !== filterBy);
    const nextFilterArray: FilterArrayItem[] = desiredIds.length
      ? [...others, { filterBy, filterValue: desiredIds, logic }]
      : others;

    setAppliedFilters(nextApplied);
    setFilterArray(nextFilterArray);
    setSecondFilterDisplay(false);
    setIsFilterOpen(false);
    emitChange(nextFilterArray, nextApplied);
  }, [
    filterBy,
    selectedOptions,
    appliedFilters,
    filterArray,
    filterLogicMap,
    emitChange,
  ]);

  const handleResetFilterGroup = useCallback(() => {
    if (!filterBy) return;
    setSelectedOptions([]);
    const nextApplied = appliedFilters.filter(
      (a) => a.filterBy !== filterBy && a.dateFilter !== filterBy
    );
    const nextFA = filterArray.filter((f) => f.filterBy !== filterBy);
    setAppliedFilters(nextApplied);
    setFilterArray(nextFA);
    emitChange(nextFA, nextApplied);
  }, [filterBy, appliedFilters, filterArray, emitChange]);

  const removeAppliedFilter = useCallback(
    (filtersToRemove: AppliedFilter | AppliedFilter[]) => {
      const toRemove = Array.isArray(filtersToRemove)
        ? filtersToRemove
        : [filtersToRemove];

      const nextApplied = appliedFilters.filter(
        (f) =>
          !toRemove.some((r) => {
            const rKey = (r.dateFilter || r.filterBy || '').trim();
            const fKey = (f.dateFilter || f.filterBy || '').trim();
            return String(r.id) === String(f.id) && rKey === fKey;
          })
      );

      const clonedFA = filterArray.map((item) => ({ ...item }));
      toRemove.forEach((r) => {
        const key = (r.dateFilter || r.filterBy || '').trim();
        const idx = clonedFA.findIndex((f) => f.filterBy === key);
        if (idx === -1) return;
        const fv = clonedFA[idx].filterValue;
        const arr = Array.isArray(fv) ? fv : [fv];
        const updated = arr.filter((x) => String(x) !== String(r.id));
        if (updated.length) {
          clonedFA[idx].filterValue = updated;
        } else {
          clonedFA.splice(idx, 1);
        }
      });

      setAppliedFilters(nextApplied);
      setFilterArray(clonedFA);
      emitChange(clonedFA, nextApplied);
    },
    [appliedFilters, filterArray, emitChange]
  );

  const handleResetAll = useCallback(() => {
    setAppliedFilters([]);
    setFilterArray([]);
    setSelectedOptions([]);
    setSecondFilterDisplay(false);
    setFilterBy('');
    emitChange([], []);
  }, [emitChange]);

  const handleAlertThresholdSelect = useCallback((payload: OnChangePayload) => {
    setSelectedOptions((prev) => {
      if (payload.type === 'add' && payload.item) {
        const item = payload.item;
        return prev.some((o) => o.id === item.id)
          ? prev
          : [...prev, { id: item.id, name: item.name }];
      }
      if (payload.type === 'remove' && payload.item) {
        const item = payload.item;
        return prev.filter((o) => o.id !== item.id);
      }
      if (payload.type === 'bulk-add' && payload.items) {
        const toAdd = payload.items.filter(
          (i) => !prev.some((o) => o.id === i.id)
        );
        return [...prev, ...toAdd.map((i) => ({ id: i.id, name: i.name }))];
      }
      if (payload.type === 'bulk-remove' && payload.items) {
        const removeIds = new Set(payload.items.map((i) => i.id));
        return prev.filter((o) => !removeIds.has(o.id));
      }
      return prev;
    });
  }, []);

  const openSubmenuAt = useCallback(
    (fb: string, el: EventTarget | null) => {
      // When clicking a pill to edit a filter, we need to:
      // 1. Populate the dropdown options from prefetchedLookup
      // 2. Pre-select the currently applied filters for this filterBy
      // 3. Set up the submenu state

      // Get display title from prefetch or fallback to static config
      const title =
        prefetchedDisplayNames[fb] ??
        fb.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

      const existingLogic = getAppliedFilterLogic(
        fb,
        appliedFilters,
        filterArray
      );
      setFilterLogicMap((prev) => ({ ...prev, [fb]: existingLogic }));

      // Check if this is a date filter
      if (dateKeys.has(fb)) {
        setFilterBy(fb);
        setFilterTitle(title);
        setIsDatePicker(true);
        setSecondaryFiltersDDL([]);
        setSecondaryFiltersSource([]);
      } else {
        // Get options from prefetch for this filter
        const options = prefetchedLookup.get(fb) ?? [];

        setFilterBy(fb);
        setFilterTitle(title);
        setIsDatePicker(false);

        // For state filters (origin_state, destination_state), options are StateRow[]
        // For other filters, options are ListItem[]
        // Both types are compatible with the dropdown components
        setSecondaryFiltersDDL(options as ListItem[]);
        setSecondaryFiltersSource(options as ListItem[]);

        // Pre-populate selectedOptions with currently applied filters for this filterBy
        const currentlyApplied = appliedFilters.filter(
          (a) => a.filterBy === fb || a.dateFilter === fb
        );
        setSelectedOptions(currentlyApplied);
      }

      // Set up the submenu anchor and display
      if (!el || !(el instanceof HTMLElement)) {
        setOpenedFromPill(false);
        setSecondFilterDisplay(true);
        return;
      }
      const r = el.getBoundingClientRect();
      setSubmenuAnchor({ left: r.left, top: r.bottom, el });
      setOpenedFromPill(true);
      setSecondFilterDisplay(true);
    },
    [
      prefetchedLookup,
      prefetchedDisplayNames,
      dateKeys,
      appliedFilters,
      filterArray,
    ]
  );

  // ── Date-range support ───────────────────────────────────────────────────────
  const filterArrayForDateRange: FilterItem[] = filterArray.map((item) => ({
    filterBy: item.filterBy,
    filterValue: Array.isArray(item.filterValue)
      ? item.filterValue.map(String)
      : [String(item.filterValue)],
  }));

  const updateFilterArrayState = useCallback(
    (model: { filterBy: string; filterValue: string[] }) => {
      const prev = filterArray.map((i) => ({ ...i }));
      const idx = prev.findIndex((i) => i.filterBy === model.filterBy);
      if (idx !== -1) {
        const existing = Array.isArray(prev[idx].filterValue)
          ? prev[idx].filterValue
          : [prev[idx].filterValue];
        prev[idx].filterValue = Array.from(
          new Set([...existing.map(String), ...model.filterValue])
        );
      } else {
        prev.push({ filterBy: model.filterBy, filterValue: model.filterValue });
      }
      setFilterArray(prev);
      return prev as FilterItem[];
    },
    [filterArray]
  );

  const { applyDateRange } = useDateRangeApplyPost({
    appliedFilters,
    filterArray: filterArrayForDateRange,
    updateFilterArrayState,
    setAppliedFilters,
    setFilterArray,
    setShowAppliedFilters: () => {},
    setSecondFilterDisplay,
    buildPayload: () => null,
    setFiltersPayload: () => {},
  });

  const handleDataFromChild = useCallback(
    (
      payload: Parameters<
        ComponentProps<typeof CalendarFilterMenu>['handleDataFromChild']
      >[0]
    ) => {
      // The calendar can also send { startDate, endDate }; map it to the range shape.
      applyDateRange(
        'rangeStart' in payload
          ? payload
          : {
              dateFilter: payload.dateFilter,
              rangeStart: payload.startDate,
              rangeEnd: payload.endDate,
            }
      );
      pendingEmitRef.current = true;
    },
    [applyDateRange]
  );
  // ─────────────────────────────────────────────────────────────────────────────

  const menu = useMemo(
    () => (
      <MainFilterMenu
        filtersDDL={NODE_GROUPED_FILTERS}
        topLevelFilters={NODE_TOP_LEVEL_FILTERS}
        handleFilterClick={handleFilterClick}
        showSearch={true}
        showGroupTitles={true}
        showDividers={true}
        dropdownMaxHeight={dynamicDropdownHeight}
      />
    ),
    [handleFilterClick]
  );

  const renderSubMenu = useCallback(
    ({ hideBackArrow }: { hideBackArrow: boolean }) => (
      <SubFilterMenu
        filterBy={filterBy}
        filterTitle={filterTitle}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        secondaryFiltersDDL={secondaryFiltersDDL}
        secondaryFiltersSource={secondaryFiltersSource}
        setSecondaryFiltersDDL={setSecondaryFiltersDDL}
        setSecondaryFiltersSource={setSecondaryFiltersSource}
        appliedFilters={appliedFilters}
        filterLogic={filterLogicMap[filterBy] || 'OR'}
        onFilterLogicChange={(logic) => {
          setFilterLogicMap((prev) => ({ ...prev, [filterBy]: logic }));
        }}
        handleSecondaryFilterSelect={(e, option) => {
          if (e?.target?.checked) {
            setSelectedOptions((prev) =>
              prev.some((item) => item.id === option.id)
                ? prev
                : [...prev, option]
            );
          } else {
            setSelectedOptions((prev) =>
              prev.filter((item) => item.id !== option.id)
            );
          }
        }}
        handleApplyFilters={handleApplyFilters}
        onResetGroup={handleResetFilterGroup}
        setSecondFilterDisplay={setSecondFilterDisplay}
        setIsFilterOpen={setIsFilterOpen}
        hideBackArrow={hideBackArrow}
        // SubFilterBody types the payload loosely; the alert filter always emits OnChangePayload.
        handleAlertThresholdSelect={(payload) =>
          handleAlertThresholdSelect(payload as OnChangePayload)
        }
        fetcherOverrides={subFilterFetcherOverrides}
        alertFilterConfigOverride={alertFilterConfig}
        token={token ?? undefined}
        dropdownMaxHeight={dynamicDropdownHeight}
      />
    ),
    [
      filterBy,
      filterTitle,
      selectedOptions,
      secondaryFiltersDDL,
      secondaryFiltersSource,
      appliedFilters,
      filterLogicMap,
      handleApplyFilters,
      handleResetFilterGroup,
      handleAlertThresholdSelect,
      subFilterFetcherOverrides,
      alertFilterConfig,
      token,
    ]
  );

  const calendarMenu = useCallback(
    ({ hideBackArrow }: { hideBackArrow: boolean }) => (
      <CalendarFilterMenu
        hideBackArrow={hideBackArrow}
        editingId={editingId}
        dateFilter={filterBy}
        handleDataFromChild={handleDataFromChild}
        filterTitle={filterTitle}
        setSecondFilterDisplay={setSecondFilterDisplay}
        setIsFilterOpen={setIsFilterOpen}
      />
    ),
    [filterBy, filterTitle, editingId, handleDataFromChild]
  );

  return (
    <div className={styles.customizationSection}>
      <div className={styles.filterToolbar}>
        <FilterDropdowns
          openedFromPill={openedFromPill}
          submenuAnchor={submenuAnchor ?? undefined}
          appliedFilters={appliedFilters}
          menu={menu}
          isFilterOpen={!isReadOnly && isFilterOpen}
          setIsFilterOpen={(v) => {
            if (!isReadOnly) setIsFilterOpen(v);
          }}
          secondFilterDisplay={!isReadOnly && secondFilterDisplay}
          setSecondFilterDisplay={(v) => {
            if (!isReadOnly) setSecondFilterDisplay(v);
          }}
          filterBy={filterBy}
          isDatePicker={isDatePicker}
          calendarMenu={calendarMenu}
          renderSubMenu={renderSubMenu}
          showAddFilter={true}
          zIndex={true}
        />
        {appliedFilters.length > 0 && (
          <button
            type='button'
            className={styles.filterResetAll}
            onClick={handleResetAll}
          >
            Reset all
          </button>
        )}
      </div>

      {appliedFilters.length > 0 && (
        <div className={styles.filterPills}>
          <AppliedFilterPills
            lookups={
              lookups as ReturnType<
                typeof import('@/hooks/useFilterLookups').default
              >
            }
            setOpenedFromPill={setOpenedFromPill}
            setEditingFilterId={setEditingId}
            openSubmenuAt={openSubmenuAt}
            appliedFilters={enrichedAppliedFilters}
            removeAppliedFilter={removeAppliedFilter}
            show={true}
            setIsFilterOpen={setIsFilterOpen}
            setSecondaryFiltersDDL={setSecondaryFiltersDDL}
            setSecondaryFiltersSource={setSecondaryFiltersSource}
            setIsDatePicker={setIsDatePicker}
            setFilterTitle={setFilterTitle}
            secondFilterDisplay={secondFilterDisplay}
            filterBy={filterBy}
            setSecondFilterDisplay={setSecondFilterDisplay}
            setFilterBy={setFilterBy}
            filterArray={filterArray}
            showSaveFilterSetLink={false}
            blockLayout={true}
          />
        </div>
      )}
    </div>
  );
};
